// src/components/admin/ColegiadoEditDialog.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Chip, CircularProgress, Typography,
    IconButton, InputAdornment, Alert, Checkbox, Divider, Tooltip
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import axios from "axios";
import { obtenerBICdesdeBanco } from "../common/Bics";
import { API_MAND } from "../Utiles/api";

/* ============================
   Validación
============================ */
const schema = Yup.object({
    nombre: Yup.string().trim().min(2, "Mínimo 2 caracteres").required("Obligatorio"),
    email: Yup.string().trim().email("Email no válido").nullable(),
    password: Yup.string()
        .trim()
        .test("pwdEmptyOrValid", "La contraseña debe tener al menos 8 caracteres.", (val) => {
            if (!val) return true;
            return String(val).length >= 8;
        }),
    repetirPassword: Yup.string()
        .trim()
        .when("password", {
            is: (val) => !!val && String(val).length > 0,
            then: (s) => s.required("Repite la contraseña").oneOf([Yup.ref("password")], "Las contraseñas no coinciden"),
            otherwise: (s) => s.notRequired(),
        }),
    fecha_alta_colegiacion: Yup.string()
        .nullable()
        .test("formato-alta", "Usa dd-mm-yyyy", (v) => !v || /^\d{2}-\d{2}-\d{4}$/.test(v)),
    fecha_baja_colegiacion: Yup.string()
        .nullable()
        .test("formato-baja", "Usa dd-mm-yyyy", (v) => !v || /^\d{2}-\d{2}-\d{4}$/.test(v)),
});

/* ============================
   Helpers
============================ */
const esVentanilla = (v) => (v || "").toUpperCase() === "VENTANILLA";

// YYYY-MM-DD (o DD/MM/YYYY) -> YYYY-MM-DD
const toISODate = (val) => {
    if (!val) return "";
    const s = String(val).trim();
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) {
        const [, d, mo, y] = m;
        return `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
    }
    return s.slice(0, 10);
};

// dd-mm-yyyy -> yyyy-mm-dd
const dmyToIso = (dmy) => {
    if (!dmy) return "";
    const m = String(dmy).trim().match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (!m) return "";
    const dd = m[1].padStart(2, "0");
    const mm = m[2].padStart(2, "0");
    const yyyy = m[3];
    return `${yyyy}-${mm}-${dd}`;
};

// yyyy-mm-dd -> dd-mm-yyyy
const isoToDmy = (iso) => {
    if (!iso) return "";
    const s = String(iso);
    const [y, m, d] = s.split("-");
    if (!y || !m || !d) return s;
    return `${d.padStart(2, "0")}-${m.padStart(2, "0")}-${y}`;
};

// Extrae el código de banco (4 cifras) de un IBAN ES
const bancoFromIban = (iban) => {
    const s = (iban || "").replace(/\s+/g, "").toUpperCase();
    if (!/^ES\d{22}$/.test(s)) return "";
    return s.slice(4, 8);
};

const ibanEsValido = (iban) => {
    const s = (iban || "").replace(/\s+/g, "").toUpperCase();
    if (s === "VENTANILLA") return false;
    if (!/^ES\d{22}$/.test(s)) return false;

    // No permitir banco "0000"
    const banco = s.slice(4, 8);
    if (banco === "0000") return false;

    // Checksum IBAN (módulo 97)
    // 1) mover 4 primeros al final
    const moved = s.slice(4) + s.slice(0, 4);
    // 2) letras -> números (A=10..Z=35)
    let converted = "";
    for (const ch of moved) {
        if (/[A-Z]/.test(ch)) {
            converted += (ch.charCodeAt(0) - 55).toString(); // A=10
        } else {
            converted += ch;
        }
    }
    // 3) calcular mod 97 sin overflow (por bloques)
    let rem = 0;
    for (let i = 0; i < converted.length; i += 7) {
        const part = rem.toString() + converted.slice(i, i + 7);
        rem = Number(part) % 97;
    }
    return rem === 1;
};

// Construye URL visible de la foto (absoluta a partir del API base)
const buildFotoUrl = (apiBase, fotoPath) => {
    if (!fotoPath) return "";
    const s = String(fotoPath).trim();
    if (/^https?:\/\//i.test(s)) return s; // ya es absoluta
    let origin = "";
    try {
        const u = new URL(apiBase);
        origin = u.origin; // sin /api
    } catch {
        origin = (apiBase || "").replace(/\/+api\/?$/i, "");
    }
    const rel = s.startsWith("/") ? s : `/${s}`;
    return `${origin}${rel}`;
};

// Hook para cargar opciones de una tabla
function useEntityOptions(url, headers) {
    const [opts, setOpts] = useState([]);
    useEffect(() => {
        if (!url) return;
        let alive = true;
        (async () => {
            try {
                const res = await axios.get(url, { headers });
                if (!alive) return;
                const data = Array.isArray(res?.data) ? res.data : (res?.data?.content ?? []);
                setOpts(
                    (data || []).map((x) => {
                        const rawId = x.id ?? x.codigo ?? x.value ?? x.key;
                        const id = typeof rawId === "number" ? rawId : (/^\d+$/.test(String(rawId)) ? Number(rawId) : rawId);
                        return { id, nombre: x.nombre ?? x.descripcion ?? x.titulo ?? String(rawId ?? "") };
                    }).filter(o => o.id !== undefined && o.id !== null)
                );
            } catch {
                setOpts([]);
            }
        })();
        return () => { alive = false; };
    }, [url, headers]);
    return { opts };
}

/* ============================
   Componente
============================ */
export default function ColegiadoEditDialog({ open, codigo, onClose, onSaved }) {
    const API = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
    const headers = useMemo(() => ({ Authorization: `Bearer ${localStorage.getItem("token")}` }), []);

    const [loading, setLoading] = useState(true);
    const [init, setInit] = useState(null);
    const [error, setError] = useState("");
    const [initialEstadoId, setInitialEstadoId] = useState("");

    const [showPwd, setShowPwd] = useState(false);
    const [showPwd2, setShowPwd2] = useState(false);

    const [imgError, setImgError] = useState(false);
    const [fotoBlobUrl, setFotoBlobUrl] = useState("");
    const [blobFor, setBlobFor] = useState("");
    const [triedAuthFetch, setTriedAuthFetch] = useState(false);

    const inputFotoRef = useRef(null);
    const [operacionFoto, setOperacionFoto] = useState(false);

    const subirFotoAdmin = async (archivo, setFieldValue) => {
        if (!archivo) return;

        const tiposPermitidos = ["image/jpeg", "image/png", "image/webp"];

        if (!tiposPermitidos.includes(archivo.type)) {
            alert("Formato no válido. Utiliza una imagen JPG, PNG o WEBP.");
            return;
        }

        if (archivo.size > 5 * 1024 * 1024) {
            alert("La imagen no puede superar los 5 MB.");
            return;
        }

        const confirmar = window.confirm(
            "¿Quieres subir esta imagen como foto del colegiado?"
        );

        if (!confirmar) {
            if (inputFotoRef.current) {
                inputFotoRef.current.value = "";
            }
            return;
        }

        try {
            setOperacionFoto(true);

            const formData = new FormData();
            formData.append("foto", archivo);

            const respuesta = await fetch(
                `${API}/api/admin/colegiados/${codigo}/foto`,
                {
                    method: "POST",
                    headers,
                    body: formData,
                }
            );

            if (!respuesta.ok) {
                const mensaje = await respuesta.text();
                throw new Error(
                    mensaje || `Error al subir la foto (${respuesta.status})`
                );
            }

            const data = await respuesta.json();

            setFieldValue("foto", data.foto || "");
            setImgError(false);
            setFotoBlobUrl(null);
            setBlobFor(null);
            setTriedAuthFetch(false);

            alert("Foto actualizada correctamente.");
        } catch (error) {
            console.error("Error al subir la foto:", error);
            alert(error.message || "No se pudo subir la foto.");
        } finally {
            setOperacionFoto(false);

            if (inputFotoRef.current) {
                inputFotoRef.current.value = "";
            }
        }
    };

    // Elimina la foto del colegiado en el servidor y actualiza Formik.
    const eliminarFotoAdmin = async (setFieldValue) => {
        if (!codigo) return;

        const confirmar = window.confirm(
            "¿Seguro que quieres eliminar la foto de este colegiado?"
        );
        if (!confirmar) return;

        try {
            setOperacionFoto(true);

            const respuesta = await fetch(
                `${API}/api/admin/colegiados/${codigo}/foto`,
                {
                    method: "DELETE",
                    headers,
                }
            );

            // El endpoint puede responder 204 (sin contenido).
            if (!respuesta.ok) {
                const mensaje = await respuesta.text();
                throw new Error(
                    mensaje || `Error al eliminar la foto (${respuesta.status})`
                );
            }

            setFieldValue("foto", "");
            setImgError(false);
            setTriedAuthFetch(false);
            setBlobFor("");

            if (fotoBlobUrl) {
                URL.revokeObjectURL(fotoBlobUrl);
            }
            setFotoBlobUrl("");

            alert("Foto eliminada correctamente.");
        } catch (error) {
            console.error("Error al eliminar la foto:", error);
            alert(error.message || "No se pudo eliminar la foto.");
        } finally {
            setOperacionFoto(false);
        }
    };

    // Mandatos
    const [mandatosDefCount, setMandatosDefCount] = useState(0);
    const [mandatosTmpCount, setMandatosTmpCount] = useState(0);
    const hasAnyMandate = (mandatosDefCount + mandatosTmpCount) > 0;
    const [creatingTmp, setCreatingTmp] = useState(false);

    // Catálogos
    const { opts: paises } = useEntityOptions(`${API}/api/paises`, headers);
    const { opts: estados } = useEntityOptions(`${API}/api/estados`, headers);
    const { opts: situaciones } = useEntityOptions(`${API}/api/situaciones-profesionales`, headers);
    const { opts: titulaciones } = useEntityOptions(`${API}/api/titulaciones-principales`, headers);
    const { opts: lugaresTrabajo } = useEntityOptions(`${API}/api/lugares-trabajo`, headers);
    const { opts: ambitosTrabajo } = useEntityOptions(`${API}/api/ambitos-trabajo`, headers);
    const { opts: especialidades } = useEntityOptions(`${API}/api/especialidades`, headers);

    /* ========== Reset imagen y estados al cambiar ficha ========== */
    useEffect(() => {
        setImgError(false);
        setTriedAuthFetch(false);
        setBlobFor("");
        if (fotoBlobUrl) URL.revokeObjectURL(fotoBlobUrl);
        setFotoBlobUrl("");
    }, [codigo, open]);

    useEffect(() => {
        setImgError(false);
        setTriedAuthFetch(false);
        setBlobFor("");
        if (fotoBlobUrl) URL.revokeObjectURL(fotoBlobUrl);
        setFotoBlobUrl("");
    }, [init?.foto]);

    useEffect(() => {
        return () => {
            if (fotoBlobUrl) URL.revokeObjectURL(fotoBlobUrl);
        };
    }, [fotoBlobUrl]);

    /* ========== Cargar colegiado ========== */
    useEffect(() => {
        if (!open || !codigo) return;
        let alive = true;
        setLoading(true);
        (async () => {
            try {
                const res = await axios.get(`${API}/api/admin/colegiados/${codigo}`, { headers });
                if (!alive) return;
                const dto = res?.data || {};
                setInitialEstadoId((dto?.estado?.id ?? dto?.estado_id ?? "").toString());
                setInit(normalizeFromDto(dto));
                setError("");
            } catch (e) {
                console.error(e);
                setError("No se pudo cargar el colegiado.");
            } finally {
                setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [open, codigo, API, headers]);

    /* ========== Consultar mandatos al entrar ========== */
    useEffect(() => {
        if (!open || !codigo) return;
        let alive = true;
        (async () => {
            // DEF: usando API_MAND.listByColegiado con prefijo API
            try {
                const urlDef = `${API}${API_MAND.listByColegiado(codigo)}`;
                const { data } = await axios.get(urlDef, { headers });
                const arr = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
                if (alive) setMandatosDefCount(arr.length || 0);
            } catch (e) {
                console.warn("DEF fetch error", e?.message || e);
                if (alive) setMandatosDefCount(0);
            }

            // TMP: endpoint específico por colegiado (si existe)
            try {
                const urlTmp = `${API}/api/mandatos/tmp/by-colegiado/${encodeURIComponent(codigo)}`;
                const { data } = await axios.get(urlTmp, { headers });
                const arr = Array.isArray(data) ? data : (Array.isArray(data?.content) ? data.content : []);
                if (alive) setMandatosTmpCount(arr.length || 0);
            } catch {
                if (alive) setMandatosTmpCount(0);
            }
        })();
        return () => { alive = false; };
    }, [open, codigo, API, headers]);

    /* ========== Normalizador DTO -> Formik initialValues ========== */
    const normalizeFromDto = (c) => {
        const pick = (a, b, def = "") => (c?.[a] ?? c?.[b] ?? def);
        const asId = (v) => {
            if (v == null) return "";
            if (typeof v === "object") return v.id ?? v.codigo ?? "";
            const n = Number(v);
            return Number.isNaN(n) ? "" : n;
        };
        const objId = (o) => (o && typeof o === "object" ? asId(o.id ?? o.codigo) : "");
        const arrIdsFlexible = (...fields) => {
            for (const f of fields) {
                const v = c?.[f];
                if (Array.isArray(v)) {
                    const parsed = v
                        .map(it => (typeof it === "object" ? (it?.id ?? it?.codigo) : Number(it)))
                        .filter(x => x != null && !Number.isNaN(x));
                    if (parsed.length) return parsed;
                }
            }
            return [];
        };
        const fecha1aIso = toISODate(pick("fecha_1a_Colegiacion", "fecha_1a_colegiacion", ""));
        const fechaAltaIso = pick("fecha_alta_colegiacion", "fechaAltaColegiacion", "");
        const fechaBajaIso = pick("fecha_baja_colegiacion", "fechaBajaColegiacion", "");

        const ibanRaw = pick("iban", "iban", "");
        const usarVentanilla = esVentanilla(ibanRaw);

        return {
            nombre: pick("nombre", "nombre", ""),
            apellido_1: pick("apellido_1", "apellido1", ""),
            apellido_2: pick("apellido_2", "apellido2", ""),
            nif: pick("nif", "nif", ""),
            email: pick("email", "email", ""),
            email_2: pick("email_2", "email2", ""),
            telefono_1: pick("telefono_1", "telefono1", ""),
            telefono_2: pick("telefono_2", "telefono2", ""),
            direccion: pick("direccion", "domicilio", ""),
            localidad: pick("localidad", "poblacion", ""),
            c_p_: pick("c_p_", "cp", ""),
            provincia: pick("provincia", "provincia", ""),
            observaciones: pick("observaciones", "observaciones", ""),
            foto: pick("foto", "foto", ""),

            provincia_nacimiento: pick("provincia_nacimiento", "provinciaNacimiento", ""),
            localidad_nacimiento: pick("localidad_nacimiento", "localidadNacimiento", ""),
            fecha_nacimiento: toISODate(pick("fecha_Nacimiento", "fecha_nacimiento", "")),

            fecha_1a_colegiacion: fecha1aIso,
            fecha_alta_colegiacion: isoToDmy(fechaAltaIso),
            fecha_baja_colegiacion: isoToDmy(fechaBajaIso),

            sexo: (pick("sexo", "sexo", "") ?? "").toString(),
            pais_id: asId(pick("paisId", "pais_id", "")),
            nacionalidad_id: asId(pick("nacionalidadId", "nacionalidad_id", "")),

            __pais_nombre: (c?.pais ?? "").toString(),
            __nacionalidad_nombre: (c?.nacionalidad ?? "").toString(),

            estado_id: asId(pick("estadoId", "estado_id", "")) || objId(c?.estado),
            __estado_nombre: (c?.estado?.nombre ?? (typeof c?.estado === "string" ? c.estado : "") ?? "").toString(),

            situacion_profesional_id:
                asId(pick("situacionProfesionalId", "situacion_profesional_id", "")) || objId(c?.situacion_profesional),
            titulacion_principal_id:
                asId(pick("titulacionPrincipalId", "titulacion_principal_id", "")) || objId(c?.titulacion_principal),

            especialidades_ids: arrIdsFlexible("especialidadesIds", "especialidades_ids", "especialidades"),
            lugares_trabajo_ids: arrIdsFlexible("lugaresTrabajoIds", "lugares_trabajo_ids", "lugares_trabajo"),
            ambitos_trabajo_ids: arrIdsFlexible("ambitosTrabajoIds", "ambitos_trabajo_ids", "ambitos_trabajo"),

            iban: usarVentanilla ? "VENTANILLA" : ibanRaw,
            bic: (c?.bic ?? "").toString().toUpperCase(),
            usarVentanilla,

            porigen: (c?.porigen ?? "").toString(),
            pdestino: (c?.pdestino ?? "").toString(),
            rol: pick("rol", "rol", ""),
            universidad: pick("universidad", "universidad", ""),
            anoFin: pick("anoFin", "ano_fin", ""),

            password: "",
            repetirPassword: "",
        };
    };

    /* ========== Guardar ========== */
    const handleSubmit = async (values, { setSubmitting }) => {
        try {
            const fechaAltaOut = values.fecha_alta_colegiacion ? dmyToIso(values.fecha_alta_colegiacion) : "";
            const fechaBajaOut = values.fecha_baja_colegiacion ? dmyToIso(values.fecha_baja_colegiacion) : "";
            const ibanOut = values.usarVentanilla ? "VENTANILLA" : (values.iban || "").replace(/\s+/g, "").toUpperCase();

            const payload = {
                nombre: values.nombre?.toUpperCase() || "",
                apellido_1: values.apellido_1?.toUpperCase() || "",
                apellido_2: values.apellido_2?.toUpperCase() || "",
                nif: values.nif?.toUpperCase() || "",
                email: values.email || "",
                email_2: values.email_2 || "",
                telefono_1: values.telefono_1 || "",
                telefono_2: values.telefono_2 || "",
                direccion: values.direccion?.toUpperCase() || "",
                localidad: values.localidad || "",
                c_p_: values.c_p_ || "",
                provincia: values.provincia || "",
                provincia_nacimiento: values.provincia_nacimiento || "",
                localidad_nacimiento: values.localidad_nacimiento || "",
                observaciones: values.observaciones || "",
                foto: values.foto || "",

                fecha_nacimiento: values.fecha_nacimiento || "",
                fecha_1a_colegiacion: values.fecha_1a_colegiacion || "",
                sexo: values.sexo || "",

                pais_id: values.pais_id || null,
                nacionalidad_id: values.nacionalidad_id || null,
                estado_id: values.estado_id || null,
                situacion_profesional_id: values.situacion_profesional_id || null,
                titulacion_principal_id: values.titulacion_principal_id || null,

                especialidades_ids: Array.isArray(values.especialidades_ids) ? values.especialidades_ids.map(Number) : [],
                lugares_trabajo_ids: Array.isArray(values.lugares_trabajo_ids) ? values.lugares_trabajo_ids.map(Number) : [],
                ambitos_trabajo_ids: Array.isArray(values.ambitos_trabajo_ids) ? values.ambitos_trabajo_ids.map(Number) : [],

                iban: ibanOut,
                bic: (values.bic || "").toUpperCase(),

                fecha_alta_colegiacion: fechaAltaOut,
                fecha_baja_colegiacion: fechaBajaOut,
                porigen: values.porigen || "",
                pdestino: values.pdestino || "",
            };

            if (values.password && values.password.trim().length >= 8) {
                payload.password = values.password.trim();
            }

            await axios.put(`${API}/api/admin/colegiados/${codigo}`, payload, { headers });
            onSaved?.();
            onClose?.();
        } catch (e) {
            console.error(e);
            alert(e?.response?.data?.error || e?.response?.data?.message || e?.message || "No se pudo guardar");
        } finally {
            setSubmitting(false);
        }
    };

    /* ========== Crear Mandato Temporal ========== */
    const crearMandatoTemporal = async (values) => {
        const iban = (values.iban || "").replace(/\s+/g, "").toUpperCase();
        const bic = (values.bic || "").toUpperCase();
        const nif = (values.nif || "").toUpperCase();

        setCreatingTmp(true);
        try {
            // Misma ruta y payload que en el alta de nuevo colegiado
            await axios.post(`${API}/api/mandatos/tmp/publico`, {
                colegiadoCodigo: Number(codigo),
                nif,
                iban,
                bic,
                fechaFirma: new Date().toISOString().slice(0, 10),
                docSepaId: null,
            }, { headers });

            setMandatosTmpCount((n) => n + 1); // ahora ya tiene TMP => oculta botón
            alert("Mandato temporal creado.");
        } catch (e) {
            const msg = e?.response?.data?.message || e?.message || "No se pudo crear el mandato temporal";
            alert(msg);
        } finally {
            setCreatingTmp(false);
        }
    };

    /* ============================
       Render
    ============================ */
    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>Editar Colegiado: {codigo}</DialogTitle>
            <DialogContent dividers>
                {loading && <CircularProgress />}
                {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

                {!loading && init && (
                    <Formik initialValues={init} validationSchema={schema} enableReinitialize onSubmit={handleSubmit}>
                        {({ values, errors, touched, handleChange, setFieldValue, isSubmitting }) => {
                            const estadoCambio = (values.estado_id ?? "").toString() !== (initialEstadoId || "");
                            const faltaAltaYBaja =
                                !(values.fecha_alta_colegiacion && /^\d{2}-\d{2}-\d{4}$/.test(values.fecha_alta_colegiacion)) &&
                                !(values.fecha_baja_colegiacion && /^\d{2}-\d{2}-\d{4}$/.test(values.fecha_baja_colegiacion));

                            // Foto
                            const fotoUrlBase = buildFotoUrl(API, values.foto);
                            const cacheBust = values.foto ? `?k=${encodeURIComponent(`${codigo}-${values.foto}`)}` : "";
                            const fotoUrl = values.foto ? `${fotoUrlBase}${cacheBust}` : "";
                            const useBlob = !!(fotoBlobUrl && blobFor === (values.foto || ""));
                            const imgSrc = useBlob ? fotoBlobUrl : fotoUrl;

                            const noTieneMandatos = !hasAnyMandate;
                            const ibanOk = ibanEsValido(values.iban);

                            return (
                                <Form>
                                    {/* Cabecera: foto + datos básicos */}
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: { xs: "100px 1fr", md: "140px 1fr" },
                                            gap: 2, alignItems: "start", mb: 2,
                                        }}
                                    >
                                        {/* Foto y controles de administración */}
                                        <Box
                                            sx={{
                                                display: "flex",
                                                flexDirection: "column",
                                                alignItems: "center",
                                                gap: 1,
                                                width: { xs: 100, md: 140 },
                                            }}
                                        >
                                            {/* Fotografía */}
                                            <Box
                                                sx={{
                                                    width: "100%",
                                                    height: { xs: 120, md: 180 },
                                                    borderRadius: 1,
                                                    overflow: "hidden",
                                                    bgcolor: "grey.100",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                }}
                                            >
                                                {!imgError && imgSrc ? (
                                                    <img
                                                        key={`${codigo}-${values.foto || ""}-${useBlob ? "blob" : "url"}`}
                                                        src={imgSrc}
                                                        alt="Foto colegiado"
                                                        style={{
                                                            width: "100%",
                                                            height: "100%",
                                                            objectFit: "cover",
                                                        }}
                                                        onError={async () => {
                                                            if (!triedAuthFetch && values.foto) {
                                                                setTriedAuthFetch(true);

                                                                try {
                                                                    const authUrl = buildFotoUrl(API, values.foto);

                                                                    const res = await axios.get(authUrl, {
                                                                        headers,
                                                                        responseType: "blob",
                                                                    });

                                                                    const newBlobUrl = URL.createObjectURL(res.data);

                                                                    if (fotoBlobUrl) {
                                                                        URL.revokeObjectURL(fotoBlobUrl);
                                                                    }

                                                                    setFotoBlobUrl(newBlobUrl);
                                                                    setBlobFor(values.foto || "");
                                                                    setImgError(false);

                                                                    return;
                                                                } catch {
                                                                    // Se mostrará el estado "Sin foto"
                                                                }
                                                            }

                                                            setImgError(true);
                                                        }}
                                                    />
                                                ) : (
                                                    <Typography
                                                        variant="caption"
                                                        sx={{ p: 1, textAlign: "center" }}
                                                    >
                                                        Sin foto
                                                    </Typography>
                                                )}
                                            </Box>

                                            {/* Selector de archivo oculto */}
                                            <input
                                                ref={inputFotoRef}
                                                type="file"
                                                accept="image/jpeg,image/png,image/webp"
                                                hidden
                                                onChange={(e) => {
                                                    const archivo = e.target.files?.[0];

                                                    if (archivo) {
                                                        subirFotoAdmin(archivo, setFieldValue);
                                                    }
                                                }}
                                            />

                                            {/* Botones de administración */}
                                            {!values.foto ? (
                                                <Button
                                                    size="small"
                                                    variant="contained"
                                                    fullWidth
                                                    disabled={operacionFoto}
                                                    onClick={() => inputFotoRef.current?.click()}
                                                >
                                                    {operacionFoto ? (
                                                        <CircularProgress size={18} color="inherit" />
                                                    ) : (
                                                        "Añadir foto"
                                                    )}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button
                                                        size="small"
                                                        variant="outlined"
                                                        fullWidth
                                                        disabled={operacionFoto}
                                                        onClick={() => inputFotoRef.current?.click()}
                                                    >
                                                        {operacionFoto ? (
                                                            <CircularProgress size={18} color="inherit" />
                                                        ) : (
                                                            "Cambiar foto"
                                                        )}
                                                    </Button>

                                                    <Button
                                                        size="small"
                                                        color="error"
                                                        variant="outlined"
                                                        fullWidth
                                                        disabled={operacionFoto}
                                                        onClick={() => eliminarFotoAdmin(setFieldValue)}
                                                    >
                                                        Eliminar foto
                                                    </Button>
                                                </>
                                            )}
                                        </Box>

                                        <Box sx={{ alignSelf: "center" }}>
                                            <Typography variant="h6" sx={{ mb: 0.5 }}>
                                                {values.apellido_1} {values.apellido_2}, {values.nombre}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">NIF: {values.nif || "—"}</Typography>
                                            {values.email && (
                                                <Typography variant="body2" color="text.secondary">Email: {values.email}</Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Datos Personales</Typography>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4,1fr)" }} gap={2} mb={2}>
                                        <TextField name="nombre" label="Nombre" value={values.nombre} onChange={handleChange}
                                            error={touched.nombre && !!errors.nombre} helperText={touched.nombre && errors.nombre} />
                                        <TextField name="apellido_1" label="Primer Apellido" value={values.apellido_1} onChange={handleChange} />
                                        <TextField name="apellido_2" label="Segundo Apellido" value={values.apellido_2} onChange={handleChange} />
                                        <TextField name="nif" label="NIF" value={values.nif} onChange={handleChange} />

                                        <TextField
                                            name="fecha_nacimiento"
                                            label="Fecha de Nacimiento"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={values.fecha_nacimiento || ""}
                                            onChange={handleChange}
                                        />
                                        <TextField name="provincia_nacimiento" label="Provincia de Nacimiento"
                                            value={values.provincia_nacimiento || ""} onChange={handleChange} />
                                        <TextField name="localidad_nacimiento" label="Localidad de Nacimiento"
                                            value={values.localidad_nacimiento || ""} onChange={handleChange} />
                                        <TextField select name="sexo" label="Sexo" value={values.sexo ?? ""} onChange={handleChange}>
                                            <MenuItem value="">—</MenuItem>
                                            <MenuItem value="0">Masculino</MenuItem>
                                            <MenuItem value="1">Femenino</MenuItem>
                                        </TextField>

                                        <TextField select name="nacionalidad_id" label="Nacionalidad"
                                            value={values.nacionalidad_id || ""}
                                            onChange={(e) => setFieldValue("nacionalidad_id", e.target.value === "" ? "" : Number(e.target.value))}
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {paises.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
                                        </TextField>

                                        <TextField select name="pais_id" label="País de Residencia"
                                            value={values.pais_id || ""}
                                            onChange={(e) => setFieldValue("pais_id", e.target.value === "" ? "" : Number(e.target.value))}
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {paises.map(p => <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>)}
                                        </TextField>
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Dirección</Typography>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "2fr 1fr 1fr 1fr" }} gap={2} mb={2}>
                                        <TextField name="direccion" label="Dirección" value={values.direccion} onChange={handleChange} />
                                        <TextField name="localidad" label="Localidad" value={values.localidad} onChange={handleChange} />
                                        <TextField name="c_p_" label="Código Postal" value={values.c_p_} onChange={handleChange} />
                                        <TextField name="provincia" label="Provincia" value={values.provincia} onChange={handleChange} />
                                    </Box>

                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Datos de Contacto</Typography>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "repeat(4,1fr)" }} gap={2} mb={2}>
                                        <TextField name="telefono_1" label="Teléfono 1" value={values.telefono_1} onChange={handleChange} />
                                        <TextField name="telefono_2" label="Teléfono 2" value={values.telefono_2} onChange={handleChange} />
                                        <TextField name="email" label="Correo Electrónico" value={values.email} onChange={handleChange}
                                            error={touched.email && !!errors.email} helperText={touched.email && errors.email} />
                                        <TextField name="email_2" label="Correo Electrónico Secundario" value={values.email_2} onChange={handleChange} />
                                    </Box>

                                    {/* Bancarios */}
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Datos Bancarios</Typography>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr" }} gap={2} mb={2}>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                            <TextField
                                                name="iban"
                                                label="IBAN (sin espacios)"
                                                value={values.iban}
                                                onChange={(e) => {
                                                    const nuevo = (e.target.value || "").replace(/\s+/g, "").toUpperCase();
                                                    setFieldValue("iban", nuevo);
                                                    if (!esVentanilla(nuevo) && /^ES\d{22}$/.test(nuevo)) {
                                                        const banco = bancoFromIban(nuevo);
                                                        const bic = (obtenerBICdesdeBanco(banco) || "").toUpperCase();
                                                        setFieldValue("bic", bic === "DESCONOCIDO" ? "" : bic);
                                                    }
                                                    if (esVentanilla(nuevo)) setFieldValue("bic", "");
                                                }}
                                                disabled={!!values.usarVentanilla}
                                                helperText={
                                                    values.usarVentanilla
                                                        ? "Modo VENTANILLA activo: no se valida IBAN y se guardará como 'VENTANILLA'."
                                                        : ""
                                                }
                                                fullWidth
                                            />
                                            <Box sx={{ whiteSpace: "nowrap" }}>
                                                <Checkbox
                                                    checked={!!values.usarVentanilla}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFieldValue("usarVentanilla", checked);
                                                        setFieldValue("iban", checked ? "VENTANILLA" : "");
                                                        setFieldValue("bic", "");
                                                    }}
                                                />
                                                <Typography variant="body2" component="span">Sin IBAN (VENTANILLA)</Typography>
                                            </Box>
                                        </Box>

                                        <TextField
                                            name="bic"
                                            label="BIC"
                                            value={values.bic || ""}
                                            onChange={(e) => setFieldValue("bic", (e.target.value || "").toUpperCase())}
                                            placeholder="Se autocalculará al introducir el IBAN. Si no se encuentra, introdúcelo manualmente."
                                            helperText="Editable. Si el IBAN cambia y el banco está en el mapa local, se autocompletará."
                                            fullWidth
                                        />
                                    </Box>

                                    {/* ====== BOTÓN CREAR MANDATO (encima de Fechas de colegiación) ====== */}
                                    {noTieneMandatos && (
                                        <Box sx={{ mb: 2 }}>
                                            <Tooltip
                                                title={
                                                    ibanOk
                                                        ? "Crear Mandato SEPA temporal con el IBAN actual"
                                                        : "Introduce un IBAN válido (ES00… sin espacios) para habilitar"
                                                }
                                                placement="top"
                                                arrow
                                            >
                                                <span>
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        size="large"
                                                        fullWidth
                                                        disabled={!ibanOk || creatingTmp}
                                                        onClick={async () => {
                                                            if (!ibanOk) return;
                                                            const ok = window.confirm(
                                                                "Se creará un Mandato SEPA TEMPORAL con el IBAN/BIC actuales. ¿Continuar?"
                                                            );
                                                            if (!ok) return;
                                                            await crearMandatoTemporal(values);
                                                        }}
                                                        sx={{ fontWeight: 800, py: 1.5, letterSpacing: 0.5 }}
                                                    >
                                                        CREAR MANDATO TEMPORAL
                                                    </Button>
                                                </span>
                                            </Tooltip>

                                            {!ibanOk && (
                                                <Typography variant="body2" color="error" sx={{ mt: 1, textAlign: "center" }}>
                                                    Introduce un IBAN válido (ES00… sin espacios) para poder crear el mandato temporal.
                                                </Typography>
                                            )}
                                        </Box>
                                    )}
                                    {/* Aviso si cambia estado y faltan fechas */}
                                    {estadoCambio && faltaAltaYBaja && (
                                        <Typography
                                            variant="h6"
                                            sx={{ gridColumn: "1 / -1", textAlign: "center", color: "error.main", fontWeight: 700, mt: 1, mb: 1 }}
                                        >
                                            RECUERDA INDICAR FECHA ALTA O BAJA COLEGIACION
                                        </Typography>
                                    )}

                                    {/* Fechas de colegiación */}
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
                                        <TextField
                                            select
                                            name="estado_id"
                                            label="Estado"
                                            value={values.estado_id || ""}
                                            onChange={(e) =>
                                                setFieldValue(
                                                    "estado_id",
                                                    e.target.value === "" ? "" : Number(e.target.value)
                                                )
                                            }
                                        >
                                            <MenuItem value="">—</MenuItem>
                                            {estados.map((est) => (
                                                <MenuItem key={est.id} value={est.id}>
                                                    {est.nombre}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        <TextField
                                            name="fecha_alta_colegiacion"
                                            label="Fecha ALTA colegiación (dd-mm-yyyy)"
                                            value={values.fecha_alta_colegiacion || ""}
                                            onChange={handleChange}
                                            placeholder="dd-mm-yyyy"
                                            error={touched.fecha_alta_colegiacion && !!errors.fecha_alta_colegiacion}
                                            helperText={touched.fecha_alta_colegiacion && errors.fecha_alta_colegiacion}
                                        />
                                        <TextField
                                            name="fecha_baja_colegiacion"
                                            label="Fecha BAJA colegiación (dd-mm-yyyy)"
                                            value={values.fecha_baja_colegiacion || ""}
                                            onChange={handleChange}
                                            placeholder="dd-mm-yyyy"
                                            error={touched.fecha_baja_colegiacion && !!errors.fecha_baja_colegiacion}
                                            helperText={touched.fecha_baja_colegiacion && errors.fecha_baja_colegiacion}
                                        />
                                        {/* NUEVO: Provincia origen */}
                                        <TextField
                                            name="porigen"
                                            label="Provincia origen"
                                            value={values.porigen || ""}
                                            onChange={handleChange}
                                        />

                                        {/* NUEVO: Provincia destino */}
                                        <TextField
                                            name="pdestino"
                                            label="Provincia destino"
                                            value={values.pdestino || ""}
                                            onChange={handleChange}
                                        />
                                    </Box>

                                    {/* Fecha 1ª Colegiación + Especialidades */}
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
                                        <TextField
                                            name="fecha_1a_colegiacion"
                                            label="Fecha 1ª Colegiación"
                                            type="date"
                                            InputLabelProps={{ shrink: true }}
                                            value={values.fecha_1a_colegiacion || ""}
                                            onChange={handleChange}
                                        />
                                        <TextField
                                            select
                                            SelectProps={{
                                                multiple: true,
                                                renderValue: (sel) => (
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                        {(sel || []).map((id) => {
                                                            const it = especialidades.find(x => x.id === id);
                                                            return <Chip key={id} label={it?.nombre ?? id} />;
                                                        })}
                                                    </Box>
                                                )
                                            }}
                                            name="especialidades_ids"
                                            label="Especialidades"
                                            value={values.especialidades_ids || []}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const arr = Array.isArray(val) ? val.map(Number) : [];
                                                setFieldValue("especialidades_ids", arr);
                                            }}
                                        >
                                            {especialidades.map(e => (
                                                <MenuItem key={e.id} value={e.id}>{e.nombre}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>

                                    {/* Lugares y Ámbitos */}
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
                                        {/* LUGAR DE TRABAJO: Autocomplete multiple con buscador */}
                                        <Autocomplete
                                            multiple
                                            options={lugaresTrabajo}
                                            // cómo identificar y mostrar
                                            getOptionLabel={(option) => option?.nombre || ""}
                                            // valor actual (array de objetos a partir de los ids que tiene Formik)
                                            value={(lugaresTrabajo || []).filter((opt) =>
                                                (values.lugares_trabajo_ids || []).some(
                                                    (id) => String(id) === String(opt.id)
                                                )
                                            )}
                                            onChange={(_, nuevoValor) => {
                                                const ids = (nuevoValor || []).map((opt) => Number(opt.id));
                                                setFieldValue("lugares_trabajo_ids", ids);
                                            }}
                                            renderTags={(tagValue, getTagProps) =>
                                                tagValue.map((option, index) => (
                                                    <Chip
                                                        {...getTagProps({ index })}
                                                        key={option.id}
                                                        label={option.nombre}
                                                    />
                                                ))
                                            }
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    label="Lugar de Trabajo"
                                                    placeholder="Escribe para buscar..."
                                                />
                                            )}
                                        />

                                        {/* Ámbito de trabajo: lo dejamos como select múltiple normal */}
                                        <TextField
                                            select
                                            SelectProps={{
                                                multiple: true,
                                                renderValue: (sel) => (
                                                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                                        {(sel || []).map((id) => {
                                                            const it = ambitosTrabajo.find(x => x.id === id);
                                                            return <Chip key={id} label={it?.nombre ?? id} />;
                                                        })}
                                                    </Box>
                                                )
                                            }}
                                            name="ambitos_trabajo_ids"
                                            label="Ámbito de Trabajo"
                                            value={values.ambitos_trabajo_ids || []}
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const arr = Array.isArray(val) ? val.map(Number) : [];
                                                setFieldValue("ambitos_trabajo_ids", arr);
                                            }}
                                        >
                                            {ambitosTrabajo.map(a => (
                                                <MenuItem key={a.id} value={a.id}>{a.nombre}</MenuItem>
                                            ))}
                                        </TextField>
                                    </Box>


                                    <Box sx={{ mb: 1 }}>
                                        <Divider sx={{ mb: 1 }} />
                                        <TextField
                                            name="observaciones"
                                            label="Observaciones"
                                            value={values.observaciones || ""}
                                            onChange={handleChange}
                                            multiline
                                            rows={4}
                                            variant="outlined"
                                            fullWidth
                                        />
                                    </Box>

                                    {/* Seguridad: contraseña */}
                                    <Typography variant="subtitle1" sx={{ mb: 1 }}>Seguridad</Typography>
                                    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1fr 1fr" }} gap={2} mb={2}>
                                        <TextField
                                            name="password"
                                            label="Nueva contraseña"
                                            type={showPwd ? "text" : "password"}
                                            value={values.password || ""}
                                            onChange={handleChange}
                                            error={touched.password && !!errors.password}
                                            helperText={touched.password && errors.password ? errors.password : "Déjalo vacío si no deseas cambiarla"}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPwd((s) => !s)} edge="end" aria-label="mostrar contraseña">
                                                            {showPwd ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                        <TextField
                                            name="repetirPassword"
                                            label="Repetir contraseña"
                                            type={showPwd2 ? "text" : "password"}
                                            value={values.repetirPassword || ""}
                                            onChange={handleChange}
                                            error={touched.repetirPassword && !!errors.repetirPassword}
                                            helperText={touched.repetirPassword && errors.repetirPassword}
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton onClick={() => setShowPwd2((s) => !s)} edge="end" aria-label="mostrar contraseña">
                                                            {showPwd2 ? <VisibilityOff /> : <Visibility />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                ),
                                            }}
                                        />
                                    </Box>

                                    {/* Aviso fechas si cambia estado */}
                                    {
                                        estadoCambio && faltaAltaYBaja && (
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                Has cambiado el <b>Estado</b>. Indica <b>fecha ALTA</b> o <b>fecha BAJA</b> de colegiación.
                                            </Alert>
                                        )
                                    }

                                    <DialogActions sx={{ px: 0 }}>
                                        <Button onClick={onClose} disabled={isSubmitting}>Cancelar</Button>
                                        <Button type="submit" variant="contained" disabled={isSubmitting}>Guardar</Button>
                                    </DialogActions>
                                </Form>
                            );
                        }}
                    </Formik>
                )}
            </DialogContent>
        </Dialog >
    );
}
